import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, Row, Col } from 'react-bootstrap';
import { Button } from '@components';

import { projectGetByIdAction } from '@app/store/reducers/project';
import {
    projectStepGetByProjectIdAction,
    projectStepUpdateStatusAction,
} from '@app/store/reducers/projectStep';
import {
    TRANG_THAI_QUY_TRINH_OPTIONS,
} from '@app/store/constants';
import { URI_INTERNAL } from '@app/utils/pathLocations';
import { toast } from 'react-toastify';
import { messageToast } from '@app/utils/define';

const toQuyTrinhStatusValue = (valueOrKey) => {
    const opt = TRANG_THAI_QUY_TRINH_OPTIONS.find(
        (o) => o.value === valueOrKey || o.key === valueOrKey
    );
    return opt ? opt.value : 0;
};

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const clampPercent = (n) => {
  const v = typeof n === 'number' && !Number.isNaN(n) ? n : 0;
  return Math.max(0, Math.min(100, v));
};

const formatPercentInt = (n) => `${Math.round(clampPercent(n))}%`;

function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('vi-VN');
}

const extractErrorMessage = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err.message) return err.message;
    if (err.title) return err.title;
    if (err.errors) {
        try {
            return Object.values(err.errors).flat().join('; ');
        } catch {
            return '';
        }
    }
    return '';
};

const ProjectDetail = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const query = useQuery();

    const idParam = query.get('id');
    const projectId = idParam ? parseInt(idParam, 10) : NaN;

    const { item: project } = useSelector((state) => state.project);
    const { items: stepsRaw, loading: stepLoading } = useSelector(
        (state) => state.projectStep
    );

    const safeReturnUrl = useMemo(() => {
    const raw = query.get('returnUrl') || '';
    if (!raw) return '';

    let decoded = raw;
    try {
        decoded = decodeURIComponent(raw);
    } catch {
        // ignore
    }

    if (decoded.startsWith('/')) return decoded;

    return '';
    }, [location.search]); 

    useEffect(() => {
        if (!Number.isNaN(projectId)) {
            dispatch(projectGetByIdAction(projectId));
            // Lấy summary để có đủ SoCanLam, SoDangLam, TienDoPhanTram...
            dispatch(projectStepGetByProjectIdAction(projectId));
        }
    }, [dispatch, projectId]);

    const teams = useMemo(() => {
        if (!project || !Array.isArray(project.teams)) return [];
        return project.teams;
    }, [project]);

    const steps = useMemo(() => {
        if (!Array.isArray(stepsRaw)) return [];
        return [...stepsRaw].sort(
            (a, b) => (a.thuTuBuoc || 0) - (b.thuTuBuoc || 0)
        );
    }, [stepsRaw]);

    const totalProgress = useMemo(() => {
        if (!steps.length) return 0;
        const sum = steps.reduce(
            (acc, s) => acc + (s.tienDoPhanTram || 0),
            0
        );
        return Math.round(sum / steps.length);
    }, [steps]);

    const handleOpenStepBoard = (step) => {
        history.push(
            `${URI_INTERNAL.PROJECT_STEP_BOARD_PATH}?projectId=${projectId}&stepId=${step.id}`
        );
    };

    const handleChangeStepStatus = async (step, newStatus) => {
        const currentStatus = toQuyTrinhStatusValue(step.status);
        if (currentStatus === newStatus) return;

        try {
            await dispatch(
                projectStepUpdateStatusAction({
                    id: step.id,
                    data: {
                        projectId: step.projectId,
                        quyTrinhId: step.quyTrinhId,
                        status: newStatus,
                        thuTuBuoc: step.thuTuBuoc,
                    },
                })
            ).unwrap();

            if (!Number.isNaN(projectId)) {
                dispatch(projectStepGetByProjectIdAction(projectId));
            }

            toast.success(
                messageToast.messageToastUpdateSuccess ||
                'Cập nhật trạng thái quy trình thành công'
            );
        } catch (err) {
            const msg = extractErrorMessage(err);
            const baseMsg =
                messageToast.messageToastUpdateError ||
                'Cập nhật trạng thái quy trình thất bại';
            toast.error(msg ? `${baseMsg}: ${msg}` : baseMsg);
        }
    };

    if (Number.isNaN(projectId)) return <p>Thiếu id dự án</p>;
    if (!project) return <p>Đang tải hoặc không tìm thấy dự án.</p>;

    return (
        <>
            <Button
                theme="secondary"
                onClick={() => history.push(safeReturnUrl || URI_INTERNAL.PROJECT_PATH)}
                className="mb-3"
            >
                ← Quay lại
            </Button>

            {/* Thông tin chung dự án */}
            <Card className="mb-3">
                <Card.Header as="h5">{project.name}</Card.Header>
                <Card.Body>
                    {project.moTa && (
                        <p className="text-muted mb-3">{project.moTa}</p>
                    )}

                    <Row>
                        <Col md={4}>
                            <strong>Thời gian</strong>
                            <p className="mb-0">
                                {formatDate(project.startDate)}{' '}
                                {project.startDate || project.endDate ? ' - ' : ''}
                                {formatDate(project.endDate)}
                            </p>
                        </Col>
                        <Col md={4}>
                            <strong>Nhóm tham gia</strong>
                            <p className="mb-0">
                                {teams.length > 0
                                    ? teams.map((t) => t.teamName).join(', ')
                                    : 'Chưa gán tổ/nhóm'}
                            </p>
                        </Col>
                        <Col md={4}>
                            <strong>Tiến độ tổng thể</strong>
                            <p className="mb-0">{totalProgress}%</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Quy trình phát triển phần mềm */}
            <Card>
                <Card.Header as="h5">Quy trình phát triển phần mềm</Card.Header>
                <Card.Body>
                    {stepLoading && <p>Đang tải danh sách bước quy trình...</p>}

                    {!stepLoading && steps.length === 0 && (
                        <p>Chưa gán bước quy trình nào cho dự án này.</p>
                    )}

                    {!stepLoading && steps.length > 0 && (
                        <div>
                            {steps.map((s, idx) => {
                                const stepProgress = clampPercent(s.tienDoPhanTram);
                                const currentStatus = toQuyTrinhStatusValue(s.status);

                                return (
                                    <div
                                        key={s.id}
                                        className="border rounded p-3 mb-3"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleOpenStepBoard(s)}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <span className="badge badge-success mr-2">
                                                    {idx + 1}
                                                </span>
                                                <strong>{s.quyTrinhName}</strong>
                                            </div>
                                            <div
                                                style={{
                                                    minWidth: 80,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                <span>{formatPercentInt(stepProgress)}</span>
                                            </div>
                                        </div>

                                        <div className="mt-3">
                                            <div
                                                className="d-inline-flex flex-wrap align-items-center"
                                                style={{
                                                    columnGap: '48px',
                                                    width: 'fit-content',
                                                    maxWidth: '100%',
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <small className="mr-2">Trạng thái:</small>
                                                {TRANG_THAI_QUY_TRINH_OPTIONS.map((opt) => (
                                                    <div
                                                        className="form-check form-check-inline mb-1"
                                                        key={opt.value}
                                                    >
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name={`step-status-${s.id}`}
                                                            id={`step-${s.id}-status-${opt.key}`}
                                                            checked={currentStatus === opt.value}
                                                            onChange={() =>
                                                                handleChangeStepStatus(s, opt.value)
                                                            }
                                                        />
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor={`step-${s.id}-status-${opt.key}`}
                                                        >
                                                            {opt.label}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div
                                            className="mt-3 d-flex flex-wrap"
                                            style={{ columnGap: '200px' }}
                                        >
                                            <div className="mb-1">
                                                Cần làm: <strong>{s.soCanLam || 0}</strong>
                                            </div>
                                            <div className="mb-1 text-primary">
                                                Đang làm: <strong>{s.soDangLam || 0}</strong>
                                            </div>
                                            <div className="mb-1 text-warning">
                                                Chờ duyệt: <strong>{s.soChoDuyet || 0}</strong>
                                            </div>
                                            <div className="mb-1 text-success">
                                                Hoàn thành: <strong>{s.soHoanThanh || 0}</strong>
                                            </div>
                                        </div>

                                        {/* Progress bar */}
                                        <div className="progress mt-3" style={{ height: 6 }}>
                                            <div
                                                className="progress-bar bg-success"
                                                role="progressbar"
                                                style={{ width: `${stepProgress}%` }}
                                                aria-valuenow={stepProgress}
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

export default ProjectDetail;
